import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Loader2, TrendingUp, TrendingDown, Target } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export default function Targets({ studentId, readOnly }) {
  const [items, setItems] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ university: "", department: "", target_tyt_net: "", target_ayt_net: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, o] = await Promise.all([
        api.get(`/targets/${studentId}`),
        api.get(`/analytics/overview/${studentId}`),
      ]);
      setItems(t.data);
      setOverview(o.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId) load();
  }, [load, studentId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.university) return toast.error("Üniversite zorunlu");
    setSaving(true);
    try {
      await api.post(`/targets/${studentId}`, {
        ...form,
        target_tyt_net: Number(form.target_tyt_net || 0),
        target_ayt_net: Number(form.target_ayt_net || 0),
      });
      toast.success("Hedef eklendi");
      setForm({ university: "", department: "", target_tyt_net: "", target_ayt_net: "" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/targets/${studentId}/${id}`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const avgTyt = overview?.avg_tyt_net || 0;
  const avgAyt = overview?.avg_ayt_net || 0;

  const gauge = (label, current, target, color) => {
    const t = Number(target || 0);
    const c = Number(current || 0);
    const pct = t > 0 ? Math.min(100, Math.round((c / t) * 100)) : 0;
    const approaching = c >= t * 0.85;
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">{label}</span>
          <span className="font-mono text-slate-800">
            {c.toFixed(1)} / <span className={color}>{t.toFixed(1)}</span>
          </span>
        </div>
        <Progress value={pct} className="h-2.5" />
        <div className={`flex items-center gap-1 text-xs ${approaching ? "text-emerald-600" : "text-rose-600"}`}>
          {approaching ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          {approaching ? "Sonuca yaklaşıyor" : "Sonuçtan uzak"} · %{pct}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-display text-xl font-bold">TYT/AYT Tercih Listesi & Hedef Net</h3>
        <p className="text-sm text-slate-500 mt-0.5">
          Hedeflenen bölüm netleri ile mevcut deneme ortalaman kıyaslanır.
        </p>
      </div>

      {!readOnly && (
        <form onSubmit={submit} className="card-ta p-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="col-span-2">
              <label className="ta-label">Üniversite</label>
              <input className="ta-input" value={form.university} onChange={(e) => setForm({ ...form, university: e.target.value })} placeholder="İTÜ" data-testid="target-university" />
            </div>
            <div className="col-span-2">
              <label className="ta-label">Bölüm</label>
              <input className="ta-input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Bilgisayar Müh." data-testid="target-department" />
            </div>
            <div>
              <label className="ta-label">Hedef TYT Net</label>
              <input className="ta-input" type="number" value={form.target_tyt_net} onChange={(e) => setForm({ ...form, target_tyt_net: e.target.value })} data-testid="target-tyt" />
            </div>
            <div>
              <label className="ta-label">Hedef AYT Net</label>
              <input className="ta-input" type="number" value={form.target_ayt_net} onChange={(e) => setForm({ ...form, target_ayt_net: e.target.value })} data-testid="target-ayt" />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid="targets-add-btn">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
            <span className="ml-1">Hedef Ekle</span>
          </Button>
        </form>
      )}

      {loading ? (
        <div className="py-10 text-center text-slate-500">
          <Loader2 className="animate-spin inline" size={22} />
        </div>
      ) : items.length === 0 ? (
        <div className="card-ta p-8 text-center text-slate-500">Henüz hedef eklenmemiş.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="targets-list">
          {items.map((t) => (
            <div key={t.id} className="card-ta p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Target className="text-indigo-600" size={20} />
                  </div>
                  <div>
                    <div className="font-display font-bold text-slate-900">{t.university}</div>
                    <div className="text-xs text-slate-500">{t.department}</div>
                  </div>
                </div>
                {!readOnly && (
                  <button onClick={() => remove(t.id)} className="text-slate-500 hover:text-rose-600" data-testid={`delete-target-${t.id}`}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="space-y-4">
                {gauge("TYT Net", avgTyt, t.target_tyt_net, "text-blue-600")}
                {gauge("AYT Net", avgAyt, t.target_ayt_net, "text-amber-600")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
